import { useState, useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { usePageTracking } from "./hooks/usePageTracking";
import { IntroProvider, useIntroSuppressed } from "./contexts/IntroContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
const Services = lazy(() => import("./pages/Services"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminQuoteCalculator = lazy(() => import("./pages/AdminQuoteCalculator"));
const AdminQuoteHistory = lazy(() => import("./pages/AdminQuoteHistory"));
const AdminTemplates = lazy(() => import("./pages/AdminTemplates"));
const AdminDemandes = lazy(() => import("./pages/AdminDemandes"));
const AdminQuoteReviews = lazy(() => import("./pages/AdminQuoteReviews"));
const AdminEditInvoice = lazy(() => import("./pages/AdminEditInvoice"));
const AdminManagement = lazy(() => import("./pages/AdminManagement"));
const AdminActivityLog = lazy(() => import("./pages/AdminActivityLog"));
const AdminEditQuote = lazy(() => import("./pages/AdminEditQuote"));
const QuoteModificationHistory = lazy(() => import("./pages/QuoteModificationHistory"));
const QuoteCalculatorTest = lazy(() => import("./pages/QuoteCalculatorTest"));
const AdminStatsDashboard = lazy(() => import("./pages/AdminStatsDashboard"));
const AdminEmailLogs = lazy(() => import("./pages/AdminEmailLogs"));
const ClientQuoteView = lazy(() => import("./pages/ClientQuoteView"));
const AdminReminders = lazy(() => import("./pages/AdminReminders"));
// Chatbot chargé en différé (embarque Streamdown/mermaid/shiki ~2,5 Mo) - hors bundle initial
const FloatingChat = lazy(() => import("./components/FloatingChat"));
import InAppBrowserBanner from "./components/InAppBrowserBanner";
const InstitutionnelPage = lazy(() => import("./pages/InstitutionnelPage"));
const InstitutionnelPortfolio = lazy(() => import("./pages/InstitutionnelPortfolio"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Arcachon = lazy(() => import("./pages/cities/Arcachon"));
const Mrignac = lazy(() => import("./pages/cities/Mrignac"));
const Pessac = lazy(() => import("./pages/cities/Pessac"));
const Talence = lazy(() => import("./pages/cities/Talence"));
const Bgles = lazy(() => import("./pages/cities/Bgles"));
const LaTestedeBuch = lazy(() => import("./pages/cities/LaTestedeBuch"));
const Libourne = lazy(() => import("./pages/cities/Libourne"));
const AndernoslesBains = lazy(() => import("./pages/cities/AndernoslesBains"));
const Gradignan = lazy(() => import("./pages/cities/Gradignan"));
const VillenavedOrnon = lazy(() => import("./pages/cities/VillenavedOrnon"));
const LeBouscat = lazy(() => import("./pages/cities/LeBouscat"));
const CitiesIndex = lazy(() => import("./pages/CitiesIndex"));
const Bordeaux = lazy(() => import("./pages/cities/Bordeaux"));
const SaintEmilion = lazy(() => import("./pages/cities/SaintEmilion"));
const CapFerret = lazy(() => import("./pages/cities/CapFerret"));
const Langon = lazy(() => import("./pages/cities/Langon"));
const Blaye = lazy(() => import("./pages/cities/Blaye"));
const LesparreMedoc = lazy(() => import("./pages/cities/LesparreMedoc"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const TestimonialForm = lazy(() => import("./pages/TestimonialForm"));
const AdminTestimonials = lazy(() => import("./pages/AdminTestimonials"));
const Reserver = lazy(() => import("./pages/Reserver"));
const AdminReservations = lazy(() => import("./pages/AdminReservations"));
const AdminDocuments = lazy(() => import("./pages/AdminDocuments"));
const AdminInvoices = lazy(() => import("./pages/AdminInvoices"));
const AdminQuotes = lazy(() => import("./pages/AdminQuotes"));
const AdminStatistics = lazy(() => import("./pages/AdminStatistics"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminParrainage = lazy(() => import("./pages/AdminParrainage"));
const AdminCalendar = lazy(() => import("./pages/AdminCalendar"));
const Disponibilites = lazy(() => import("./pages/Disponibilites"));
const AdminPaymentReminders = lazy(() => import("./pages/AdminPaymentReminders"));
const NewsletterAdmin = lazy(() => import("./pages/admin/NewsletterAdmin"));
const AdminTarifs = lazy(() => import("./pages/AdminTarifs"));
const AdminParametres = lazy(() => import("./pages/AdminParametres"));
const AdminA11yWidget = lazy(() => import("./components/AdminA11yWidget"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const Tarifs = lazy(() => import("./pages/Tarifs"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const PolitiqueCookies = lazy(() => import("./pages/PolitiqueCookies"));
import CookieBanner from "./components/CookieBanner";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import PageTransition from "./components/PageTransition";
import CinematicCursor from "./components/CinematicCursor";
import CinematicLoader from "./components/CinematicLoader";

function Router() {
  // Envoie un page_view GA4 à chaque changement de route (sinon la navigation
  // interne de la SPA n'est jamais mesurée).
  usePageTracking();

  // Widget d'accessibilite : uniquement sur les ecrans d'administration.
  const [emplacement] = useLocation();
  const dansAdmin = emplacement.startsWith("/admin");

  // make sure to consider if you need authentication for certain routes
  return (
    <Suspense fallback={null}>
    {dansAdmin && <AdminA11yWidget />}
    <Switch>
      <Route path={"/"} component={Home} />
      <Route path={"/services"} component={Services} />
      <Route path={"/portfolio"} component={Portfolio} />
      <Route path={"/category"} component={CategoryPage} />
      <Route path={"/mariage"} component={CategoryPage} />
      <Route path={"/immobilier"} component={CategoryPage} />
      <Route path={"/entreprises"} component={CategoryPage} />
      <Route path={"/associations"} component={CategoryPage} />
      <Route path={"/association"} component={CategoryPage} />
      <Route path={"/entreprise"} component={CategoryPage} />
      <Route path={"/institutionnel-categorie"} component={CategoryPage} />
      <Route path={"/experts-judiciaires"} component={CategoryPage} />
      <Route path={"/institutionnel"} component={InstitutionnelPage} />
      <Route path={"/institutionnel/portfolio"} component={InstitutionnelPortfolio} />
      <Route path={"/blog"} component={Blog} />
      <Route path={"/blog/:id"} component={BlogArticle} />
      <Route path={"/villes"} component={CitiesIndex} />
      <Route path={"/temoignages"} component={Testimonials} />
      <Route path={"/testimonials"} component={Testimonials} />
      <Route path={"/soumettre-temoignage"} component={TestimonialForm} />
      <Route path={"/admin/temoignages"} component={AdminTestimonials} />
      <Route path={"/reserver"} component={Reserver} />
      <Route path={"/admin/reservations"} component={AdminReservations} />
      <Route path={"/admin/documents"} component={AdminDocuments} />
      <Route path={"/admin/factures"} component={AdminInvoices} />
      <Route path={"/admin/devis"} component={AdminQuotes} />
      <Route path={"/ville/arcachon"} component={Arcachon} />
      <Route path={"/ville/merignac"} component={Mrignac} />
      <Route path={"/ville/pessac"} component={Pessac} />
      <Route path={"/ville/talence"} component={Talence} />
      <Route path={"/ville/begles"} component={Bgles} />
      <Route path={"/ville/la-teste-de-buch"} component={LaTestedeBuch} />
      <Route path={"/ville/libourne"} component={Libourne} />
      <Route path={"/ville/andernos-les-bains"} component={AndernoslesBains} />
      <Route path={"/ville/gradignan"} component={Gradignan} />
      <Route path={"/ville/villenave-dornon"} component={VillenavedOrnon} />
      <Route path={"/ville/le-bouscat"} component={LeBouscat} />
      <Route path={"/ville/bordeaux"} component={Bordeaux} />
      <Route path={"/ville/saint-emilion"} component={SaintEmilion} />
      <Route path={"/ville/cap-ferret"} component={CapFerret} />
      <Route path={"/ville/langon"} component={Langon} />
      <Route path={"/ville/blaye"} component={Blaye} />
      <Route path={"/ville/lesparre-medoc"} component={LesparreMedoc} />
      <Route path={"/admin/login"} component={AdminLogin} />
      <Route path={"/dashboard"} component={AdminDashboard} />
      <Route path={"/admin/dashboard"} component={AdminDashboard} />
      <Route path={"/admin/devis/nouveau"} component={AdminQuoteCalculator} />
      <Route path={"/admin/devis/historique"} component={AdminQuoteHistory} />
      <Route path={"/admin/demandes"} component={AdminDemandes} />
      <Route path={"/admin/devis/avis"} component={AdminQuoteReviews} />
      <Route path={"/admin/devis/editer-facture"} component={AdminEditInvoice} />      <Route path={"/admin/devis/modifier"} component={AdminEditQuote} />
      <Route path={"/admin/devis/historique-modifications"} component={QuoteModificationHistory} />
      <Route path={"/admin/templates"} component={AdminTemplates} />
      <Route path={"/admin/gestion-admins"} component={AdminManagement} />
      <Route path={"/admin/journal-activite"} component={AdminActivityLog} />
      <Route path={"/admin/statistiques"} component={AdminStatistics} />
      <Route path={"/admin/stats-dashboard"} component={AdminStatsDashboard} />
      <Route path={"/admin/emails"} component={AdminEmailLogs} />
      <Route path={"/admin/rappels"} component={AdminReminders} />
      <Route path={"/devis/:shareToken"} component={ClientQuoteView} />
      <Route path={"/test/calculator"} component={QuoteCalculatorTest} />
      <Route path={"/auth/callback"} component={AuthCallback} />
      <Route path={"/admin/utilisateurs"} component={AdminUsers} />
      <Route path={"/admin/parrainage"} component={AdminParrainage} />
      <Route path={"/admin/calendrier"} component={AdminCalendar} />
      <Route path={"/disponibilites"} component={Disponibilites} />
      <Route path={"/admin/rappels-paiement"} component={AdminPaymentReminders} />
      <Route path={"/admin/newsletter"} component={NewsletterAdmin} />
      <Route path={"/admin/tarifs"} component={AdminTarifs} />
      <Route path={"/admin/parametres"} component={AdminParametres} />
      <Route path={"/tarifs"} component={Tarifs} />
      <Route path={"/mentions-legales"} component={MentionsLegales} />
      <Route path={"/politique-confidentialite"} component={PolitiqueConfidentialite} />
      <Route path={"/politique-cookies"} component={PolitiqueCookies} />
      <Route path={"/404"} component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
    </Suspense>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function AppShell() {
  // Une page peut demander à ne pas déclencher l'intro (ex : la 404).
  const introSuppressed = useIntroSuppressed();

  // Loader cinématique - affichage de l'intro.
  // FORCE_EVERY_TIME = true : à chaque connexion (phase de test).
  // Passer à false = une fois par jour (penser à aligner index.html aussi).
  const FORCE_EVERY_TIME = false;
  const today = new Date().toISOString().slice(0, 10); // AAAA-MM-JJ
  const [loaderDone, setLoaderDone] = useState(() => {
    if (FORCE_EVERY_TIME) return false;
    // La decision de sauter l'intro est prise dans le script inline de index.html
    // (premier passage, deja vue aujourd'hui, ou « animations reduites ») et
    // exposee via window.__veaSkipIntro, pour que React, le splash et le LCP
    // restent parfaitement synchronises. Le bouton « Intro » reste disponible.
    if (typeof window !== "undefined") {
      const skip = (window as unknown as { __veaSkipIntro?: boolean }).__veaSkipIntro;
      if (typeof skip === "boolean") return skip;
      // Repli si le script inline n'a pas tourne.
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return true;
      return localStorage.getItem("vea_intro_date") === today;
    }
    return false;
  });

  // Relecture de l'intro à la demande (bouton Intro)
  const [introRun, setIntroRun] = useState(0); // clé de remontage
  const [introSound, setIntroSound] = useState(false);

  const handleLoaderComplete = () => {
    localStorage.setItem("vea_intro_date", today);
    setLoaderDone(true);
    setIntroSound(false);
  };

  const replayIntro = () => {
    setIntroSound(true); // clic = geste utilisateur -> son autorisé
    setIntroRun((n) => n + 1);
    setLoaderDone(false);
  };

  // Quand l'intro est sautee, le splash d'index.html reste peint pour servir de
  // premier rendu : il porte le logo, qui est l'element LCP. C'est ici qu'on le
  // retire, une fois React monte — jamais avant, sinon la page reste vide
  // jusqu'au premier rendu et le LCP attend le bundle.
  // Un double requestAnimationFrame garantit que le rendu React est peint avant
  // le retrait : sans lui, un cadre vide peut s'intercaler et faire clignoter.
  useEffect(() => {
    if (!loaderDone) return;
    const splash = document.getElementById("vea-splash");
    if (!splash?.hasAttribute("data-statique")) return;
    let frame = 0;
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => splash.parentNode?.removeChild(splash));
    });
    return () => cancelAnimationFrame(frame);
  }, [loaderDone]);

  // Monte le chatbot après l'affichage de la page (sort la lib lourde du chemin critique)
  const [chatReady, setChatReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setChatReady(true), 1500);
    return () => clearTimeout(t);
  }, []);

  return (
    <ErrorBoundary>
      {/* Loader cinématique - auto à chaque connexion + relecture via le bouton Intro */}
      {!loaderDone && !introSuppressed && (
        <CinematicLoader key={introRun} startWithSound={introSound} onComplete={handleLoaderComplete} />
      )}

      {/* Bouton Intro - opposé au chatbot (bas gauche), pour revoir l'intro avec le son */}
      {loaderDone && !introSuppressed && (
        <button
          onClick={replayIntro}
          aria-label="Revoir l'introduction"
          className="fixed bottom-6 left-6 z-50 flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-all duration-300 hover:scale-105"
          style={{
            background: "rgba(13,13,13,0.85)",
            border: "1px solid var(--vea-or, #C9A84C)",
            color: "var(--vea-or, #C9A84C)",
            fontFamily: "'Montserrat', sans-serif",
            borderRadius: "999px",
            backdropFilter: "blur(8px)",
            boxShadow: "0 4px 18px rgba(0,0,0,0.35)",
          }}
        >
          <span style={{ fontSize: "0.95em" }}>▶</span> Intro
        </button>
      )}

      <CinematicCursor />
      <CookieConsentProvider>
        <ThemeProvider
          defaultTheme="light"
          // switchable
        >
          <TooltipProvider>
            <Toaster />
            <InAppBrowserBanner />
            <PageTransition>
              <Router />
            </PageTransition>
            {chatReady && (
              <Suspense fallback={null}>
                <FloatingChat />
              </Suspense>
            )}
            <CookieBanner />
          </TooltipProvider>
        </ThemeProvider>
      </CookieConsentProvider>
    </ErrorBoundary>
  );
}

function App() {
  // Le provider enveloppe AppShell : un composant ne peut pas consommer un
  // contexte qu'il fournit lui-même.
  return (
    <IntroProvider>
      <AppShell />
    </IntroProvider>
  );
}

export default App;
